// Plik: frontend/src/components/admin/AdminLayout.js
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, AppBar, Toolbar, Typography, Button,
  IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, useMediaQuery,
  useTheme, Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 260;

const navItems = [
  { title: 'Dashboard', path: '/admin', icon: <DashboardIcon />, exact: true },
  { title: 'Uczestnicy', path: '/admin/uczestnicy', icon: <PeopleIcon /> },
  { title: 'Terminy', path: '/admin/terminy', icon: <CalendarMonthIcon /> },
  { title: 'Statystyki', path: '/admin/statystyki', icon: <BarChartIcon /> },
  { title: 'Ustawienia', path: '/admin/ustawienia', icon: <SettingsIcon /> },
];

const AdminLayout = ({ children, title, maxWidth = 'lg' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const isDashboard = location.pathname === '/admin';

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo / Branding */}
      <Box
        sx={{
          p: 2.5,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: '#fff',
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.5px' }}>
          Panel Admin
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          System Rejestracji
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1.5, py: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) setDrawerOpen(false);
              }}
              selected={isActive(item)}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                py: 1.2,
                px: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isActive(item) ? 'primary.main' : 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive(item) ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Logout at bottom */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'error.main',
              color: 'error.main',
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            },
          }}
        >
          Wyloguj się
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fff',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: '#fff',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {!isDashboard && (
              <IconButton
                onClick={() => navigate('/admin')}
                sx={{ mr: 1.5 }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Typography variant="h6" component="h1" fontWeight={600} sx={{ flex: 1 }}>
              {title || 'Panel Administratora'}
            </Typography>

            <Chip
              label="Admin"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Container maxWidth={maxWidth} sx={{ py: 4, flex: 1 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLayout;
